import PropTypes from 'prop-types';
import { Fragment } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
//
import Icon from './Icon';
import { RADIUS, sunkenOf } from 'src/theme/universe/brand';
import { glyphTileSx } from 'src/theme/universe/surfaceSx';
import { DEPARTMENT_INKS, inkTintOf } from 'src/theme/universe/tones';

// ----------------------------------------------------------------------
// WHAT A STORED FILE AND A VAULT FOLDER ARE, as a reader sees them: the
// vault's folder table and file cards and the public folder page draw the
// same glyphs, inks and names. Moved here from the vault's FileCard and
// FolderTableRow (29/10/2026) when the public page became the second reader;
// both files re-export them, so their importers are unchanged.
//
// WHAT KIND OF FILE IT IS reads before its name: a PDF, a Word file, a CAT
// file, a spreadsheet each wear their own glyph in their own colour
// (DESIGN.md §4 "Categorical colour": a few learnable FAMILIES, never one hue
// per extension, and never a hue a state owns — a PDF is rose, not the red of
// an error). The inks are the measured department inks (§4), borrowed for
// their hue and their contrast, never for their meaning.

const FAMILIES = {
  word: { glyph: 'DocumentText', ink: DEPARTMENT_INKS.projects },
  pdf: { glyph: 'DocumentText', ink: DEPARTMENT_INKS.growth },
  sheet: { glyph: 'TableDocument', ink: DEPARTMENT_INKS.finance },
  slides: { glyph: 'PresentionChart', ink: DEPARTMENT_INKS.marketing },
  image: { glyph: 'Image', ink: DEPARTMENT_INKS.people },
  artwork: { glyph: 'DocumentSketch', ink: DEPARTMENT_INKS.people },
  video: { glyph: 'VideoPlay', ink: DEPARTMENT_INKS.support },
  audio: { glyph: 'MusicSquare', ink: DEPARTMENT_INKS.support },
  cat: { glyph: 'DocumentCode', ink: DEPARTMENT_INKS.accounts },
  archive: { glyph: 'Archive', ink: DEPARTMENT_INKS.board },
  text: { glyph: 'Note', ink: null },
  other: { glyph: 'Document', ink: null }
};

const EXTENSIONS = {
  word: ['doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'rtf', 'pages'],
  pdf: ['pdf'],
  sheet: ['xls', 'xlsx', 'xlsm', 'csv', 'ods', 'numbers'],
  slides: ['ppt', 'pptx', 'pps', 'ppsx', 'key', 'odp'],
  image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tif', 'tiff', 'heic'],
  artwork: ['ai', 'psd', 'indd', 'idml', 'eps'],
  video: ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', 'mpg'],
  audio: ['mp3', 'wav', 'aac', 'm4a', 'aif', 'flac', 'ogg'],
  cat: [
    'sdlxliff',
    'xliff',
    'xlf',
    'mqxliff',
    'tmx',
    'tbx',
    'sdltm',
    'sdltb',
    'sdlproj',
    'sdlppx',
    'sdlrpx',
    'xml',
    'json',
    'html',
    'htm'
  ],
  archive: ['zip', 'rar', '7z', 'tar', 'gz'],
  text: ['txt', 'md', 'srt', 'vtt']
};

const FAMILY_OF = Object.entries(EXTENSIONS).reduce((all, [family, list]) => {
  list.forEach((ext) => {
    all[ext] = family;
  });
  return all;
}, {});

/** The last part of a stored address, decoded for a reader. */
export const fileNameOf = (url) => {
  const last = String(url || '')
    .split('?')[0]
    .split('/')
    .pop();
  try {
    return decodeURIComponent(last);
  } catch (error) {
    return last;
  }
};

/**
 * A stored name with a place to break after each `_` and `-`: the names have
 * no spaces, so a line used to break wherever a letter fell — and left the
 * "x" of ".docx" alone on the second line. Never after the dot, which would
 * strand the extension the same way.
 */
export const breakableName = (name) =>
  String(name || '')
    .split(/([_-])/)
    .map((part, at) =>
      at % 2 ? (
        <Fragment key={at}>
          {part}
          <wbr />
        </Fragment>
      ) : (
        part
      )
    );

/** The extension of a file name, lower case, or ''. */
export const extensionOf = (name) => {
  const at = String(name || '').lastIndexOf('.');
  return at > 0 ? name.slice(at + 1).toLowerCase() : '';
};

/** The family a file belongs to: its glyph and its ink (null = neutral). */
export const fileKindOf = (name) =>
  FAMILIES[FAMILY_OF[extensionOf(name)]] || FAMILIES.other;

FileKindTile.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number
};

/** The kind of a file as a tile: its glyph on its family's own wash. */
export function FileKindTile({ name, size = 40 }) {
  const theme = useTheme();
  const kind = fileKindOf(name);
  const ink = kind.ink
    ? kind.ink[theme.palette.mode === 'light' ? 'light' : 'dark']
    : theme.palette.text.secondary;
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: `${RADIUS.small}px`,
        color: ink,
        bgcolor: inkTintOf(theme, ink, 'label'),
        boxShadow: `inset 0 0 0 1px ${inkTintOf(theme, ink, 'line')}`
      }}
    >
      <Icon name={kind.glyph} size={Math.round(size * 0.5)} variant="Bold" />
    </Box>
  );
}

// ----------------------------------------------------------------------
// THE FOLDER IS NAMED, NOT SPELLED: the stored key (`project_manager`) stays
// in the address and in the client's link; the reader is shown the name the
// rest of the portal already uses for it ("Project manager"), with the glyph
// of what the folder is FOR — the client's source, the translation, the
// check, the PM's desk, the delivery that went out — drawn from the workflow
// vocabulary of §10, so the same thing is the same picture everywhere.

const FOLDERS = {
  original: { label: 'Original', glyph: 'DocumentText' },
  translated: { label: 'Translated', glyph: 'Translate' },
  Proofreaded: { label: 'Proofread', glyph: 'Verify' },
  project_manager: { label: 'Project manager', glyph: 'Kanban' },
  Project_Manager: { label: 'Project manager', glyph: 'Kanban' },
  // the outputs of the formatting step: Layout's Word, the formatter's
  // rebuilt file (report 19/10/2026, UNI-TR-16)
  formatted: { label: 'Formatted', glyph: 'RulerPen' },
  client_delivery: { label: 'Client delivery', glyph: 'Send2' },
  Support_Material: { label: 'Support material', glyph: 'Paperclip' },
  technician_Material: { label: 'Technician material', glyph: 'Headphone' }
};

/** The name a folder is shown under: "Client delivery" for `client_delivery`. */
export const folderLabelOf = (key) => {
  if (FOLDERS[key]) return FOLDERS[key].label;
  const words = String(key || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Folder';
};

/** The glyph of what a folder is for; a folder of its own is a folder. */
export const folderGlyphOf = (key) => FOLDERS[key]?.glyph || 'Folder';

FolderTile.propTypes = {
  folder: PropTypes.string,
  empty: PropTypes.bool,
  size: PropTypes.number
};

/** The folder's glyph on the accent's own wash; an empty folder is quiet. */
export function FolderTile({ folder, empty = false, size = 36 }) {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        ...glyphTileSx(theme, size),
        ...(empty && {
          color: 'text.secondary',
          bgcolor: sunkenOf(theme)
        })
      }}
    >
      <Icon
        name={folderGlyphOf(folder)}
        size={Math.round(size / 2)}
        variant={empty ? 'Linear' : 'Bold'}
      />
    </Box>
  );
}

/** The time of a stored date — an ISO string or epoch milliseconds — or 0. */
export const timeOf = (value) => {
  if (!value) return 0;
  const at = /^\d+$/.test(String(value))
    ? Number(value)
    : new Date(value).getTime();
  return Number.isNaN(at) ? 0 : at;
};
